import { z } from 'zod';
import { ColorSchema } from './style';

/**
 * Profile cards 2.0 (module API 4.10): a card is HTML and CSS of a module without scripts. The platform
 * draws it on its own sandbox page, handles its interactive elements (`data-vp-*`) and live data, keeps
 * its state in the profile, and frames it with a look (background, motion, opacity, blur, radius,
 * border, shadow) that the module and the person set.
 */

export const CARD_LIMITS = {
  /** Cards one module creates at run time. */
  cards: 20,
  /** HTML of one card, characters. */
  html: 200_000,
  /** Pictures inside one card (`data:` addresses) and a background picture, bytes of their text. */
  images: 2_000_000,
  /** Keys of a card's state, and the size of one string value. */
  stateKeys: 64,
  stateValue: 4000,
} as const;

/** What a card uses of the platform; a card declares it (`uses`) and nothing else works in it. */
export const CardUseSchema = z.enum([
  'action',
  'link',
  'toggle',
  'counter',
  'note',
  'bind',
  'live:time',
  'live:timer',
  'live:countdown',
  'live:profile',
  'anim:appear',
  'anim:pulse',
  'anim:sway',
  'anim:spin',
  'anim:shimmer',
  'anim:custom',
  'font',
  'image',
  'look:image',
  'look:motion',
]);
export type CardUse = z.infer<typeof CardUseSchema>;

export const CARD_EFFECTS = ['appear', 'pulse', 'sway', 'spin', 'shimmer'] as const;
export const CARD_MOTIONS = ['none', 'gradient', 'particles', 'waves', 'shine'] as const;

const GradientStop = z.object({ color: ColorSchema, at: z.number().min(0).max(1) }).strict();

export const CardBackgroundSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('color'), color: ColorSchema }).strict(),
  z.object({ kind: z.literal('gradient'), type: z.enum(['linear', 'radial']), angle: z.number().min(0).max(360).optional(), stops: z.array(GradientStop).min(2).max(5) }).strict(),
  z
    .object({
      kind: z.literal('image'),
      /** A PNG, JPEG or WebP picture as a `data:` address. */
      src: z.string().max(CARD_LIMITS.images).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/),
      fit: z.enum(['cover', 'contain', 'tile']),
    })
    .strict(),
]);
export type CardBackground = z.infer<typeof CardBackgroundSchema>;

/** The look of a card (or of the profile's tray); every field is optional — the platform's look fills the gaps. */
export const CardLookSchema = z
  .object({
    background: CardBackgroundSchema.nullable().optional(),
    motion: z.enum(CARD_MOTIONS).optional(),
    opacity: z.number().min(0).max(1).optional(),
    blur: z.number().min(0).max(24).optional(),
    radius: z.number().min(0).max(32).optional(),
    border: z.object({ color: ColorSchema, width: z.number().min(1).max(4) }).strict().nullable().optional(),
    shadow: z.enum(['none', 'soft', 'strong']).optional(),
  })
  .strict();
export type CardLook = z.infer<typeof CardLookSchema>;

/** One value of a card's state. */
export const CardStateValueSchema = z.union([z.boolean(), z.number().finite(), z.string().max(CARD_LIMITS.stateValue)]);
export type CardStateValue = z.infer<typeof CardStateValueSchema>;
export const CardStateKeySchema = z.string().regex(/^[a-z0-9-]{1,32}$/);
export const CardStateSchema = z.record(CardStateKeySchema, CardStateValueSchema).refine((s) => Object.keys(s).length <= CARD_LIMITS.stateKeys, `at most ${CARD_LIMITS.stateKeys} keys`);
export type CardState = Record<string, CardStateValue>;

/** What the person did on a card (event `profileCard.input`). */
export const CardInputSchema = z.union([
  z.object({ action: z.string().regex(/^[\w-]{1,64}$/) }).strict(),
  z.object({ key: CardStateKeySchema, value: CardStateValueSchema }).strict(),
]);
export type CardInput = z.infer<typeof CardInputSchema>;

/**
 * The platform features a card's HTML relies on — read from its markup and styles. Used to check a
 * card against what it declared (`uses`) and to show the person what a card does.
 */
export function cardUsesOf(html: string): Set<CardUse> {
  const out = new Set<CardUse>();
  const attr = (name: string) => new RegExp(`\\sdata-vp-${name}(\\s*=|[\\s>/])`, 'i').test(html);
  if (attr('action')) out.add('action');
  if (attr('link')) out.add('link');
  if (attr('toggle')) out.add('toggle');
  if (attr('counter')) out.add('counter');
  if (attr('note')) out.add('note');
  if (attr('bind')) out.add('bind');
  for (const m of html.matchAll(/\sdata-vp-live\s*=\s*["']?([a-z]+)/gi)) {
    const v = m[1].toLowerCase();
    if (v === 'time' || v === 'date' || v === 'weekday') out.add('live:time');
    else if (v === 'timer') out.add('live:timer');
    else if (v === 'countdown') out.add('live:countdown');
    else if (v === 'profile') out.add('live:profile');
    else if (v === 'bind') out.add('bind');
  }
  for (const m of html.matchAll(/\bvp-(?:anim|on-hover|on-press|on-change)[-:]([a-z]+)/gi)) {
    const e = m[1].toLowerCase();
    if ((CARD_EFFECTS as readonly string[]).includes(e)) out.add(`anim:${e}` as CardUse);
  }
  // styles of the module's own: animations and transitions, fonts, pictures
  if (/@keyframes|(^|[\s;{"'])(animation|transition)(-[a-z-]+)?\s*:/i.test(stylesOf(html))) out.add('anim:custom');
  if (/@font-face/i.test(html)) out.add('font');
  if (/data:image\//i.test(html)) out.add('image');
  return out;
}

/** The CSS of a document: `<style>` elements and `style` attributes. */
function stylesOf(html: string): string {
  const parts: string[] = [];
  for (const m of html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)) parts.push(m[1]);
  for (const m of html.matchAll(/\sstyle\s*=\s*("([^"]*)"|'([^']*)')/gi)) parts.push(';' + (m[2] ?? m[3] ?? ''));
  return parts.join('\n');
}

/** The features a look relies on. */
export function lookUsesOf(look: CardLook | undefined | null): Set<CardUse> {
  const out = new Set<CardUse>();
  if (look?.background?.kind === 'image') out.add('look:image');
  if (look?.motion && look.motion !== 'none') out.add('look:motion');
  return out;
}

/** Everything the manifest's profile cards of a module declare they use (shown with its permissions). */
export function manifestCardUses(m: { contributes: { profileCards?: { uses?: readonly CardUse[] }[] } }): CardUse[] {
  const all = new Set<CardUse>();
  for (const c of m.contributes.profileCards ?? []) for (const u of c.uses ?? []) all.add(u);
  return CardUseSchema.options.filter((u) => all.has(u));
}

/** What a card uses but did not declare (an empty list — the card is in order). */
export function undeclaredUses(declared: readonly CardUse[], html: string, look?: CardLook | null): CardUse[] {
  const have = new Set(declared);
  return [...cardUsesOf(html), ...lookUsesOf(look)].filter((u) => !have.has(u));
}
